import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Icon,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core"
import React from "react"
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_SHADOW,
  COLOR_SUCCESS,
  COLOR_SUCCESS_BG,
  COLOR_TEXT_MUTED,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
  COLOR_WHITE,
} from "./constants"

interface ExportReportDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (emails: string[]) => Promise<void>
  title: string
  description: string
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const ExportReportDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
}: ExportReportDialogProps) => {
  const [emails, setEmails] = React.useState<string[]>([])
  const [newEmail, setNewEmail] = React.useState<string>("")
  const [emailError, setEmailError] = React.useState<string>("")
  const [sending, setSending] = React.useState<boolean>(false)
  const [success, setSuccess] = React.useState<boolean>(false)

  const handleClose = () => {
    if (sending) return
    setEmails([])
    setNewEmail("")
    setEmailError("")
    setSuccess(false)
    onClose()
  }

  const handleAddEmail = () => {
    const trimmed = newEmail.trim()
    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError("Ingrese un correo válido")
      return
    }
    if (emails.includes(trimmed)) {
      setEmailError("Este correo ya fue agregado")
      return
    }
    setEmails([...emails, trimmed])
    setNewEmail("")
    setEmailError("")
  }

  const handleConfirm = async () => {
    setSending(true)
    try {
      await onConfirm(emails)
      setSuccess(true)
      setEmails([])
      setNewEmail("")
    } catch {
      setEmailError("Ocurrió un error al enviar la solicitud")
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      {!success ? (
        <>
          <DialogTitle>
            <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Icon style={{ color: COLOR_PRIMARY }}>download</Icon>
              {title}
            </Box>
          </DialogTitle>

          <DialogContent>
            <Typography variant="body2" gutterBottom style={{ color: COLOR_TEXT_SECONDARY }}>
              {description}
            </Typography>
            <Typography variant="body2" style={{ color: COLOR_TEXT_SECONDARY, marginBottom: 12 }}>
              El proceso es asíncrono — recibirás el reporte en el
              {emails.length === 1 ? " correo" : " o los correos"} indicados
              una vez esté listo.
            </Typography>

            {emails.length === 0 && (
              <Typography color="error" variant="body2" style={{ marginBottom: 8 }}>
                Debe agregar al menos un correo para continuar
              </Typography>
            )}

            <Box display="flex" flexWrap="wrap" style={{ marginBottom: 8 }}>
              {emails.map((email) => (
                <Box key={email} style={{ marginRight: 6, marginBottom: 6 }}>
                  <Chip
                    label={email}
                    size="small"
                    onDelete={() => setEmails(emails.filter((e) => e !== email))}
                  />
                </Box>
              ))}
            </Box>

            <TextField
              label="Agregar correo"
              fullWidth
              size="small"
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value)
                setEmailError("")
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddEmail()
              }}
              error={!!emailError}
              helperText={emailError}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Agregar correo">
                      <IconButton size="small" aria-label="agregar correo" onClick={handleAddEmail}>
                        <Icon>add</Icon>
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} disabled={sending}>
              Cancelar
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={emails.length === 0 || sending}
              onClick={handleConfirm}
              startIcon={
                sending ? (
                  <CircularProgress color="inherit" size={18} />
                ) : (
                  <Icon>send</Icon>
                )
              }
            >
              Generar y Enviar
            </Button>
          </DialogActions>
        </>
      ) : (
        <Fade in={success}>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 32px 40px",
              gap: 16,
              textAlign: "center",
            }}
          >
            <Box
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                backgroundColor: COLOR_SUCCESS_BG,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon style={{ fontSize: 40, color: COLOR_SUCCESS }}>check_circle</Icon>
            </Box>
            <Typography variant="h6" style={{ fontWeight: 700, color: COLOR_TEXT_PRIMARY }}>
              ¡Solicitud enviada!
            </Typography>
            <Typography variant="body2" style={{ color: COLOR_TEXT_MUTED, maxWidth: 320 }}>
              El reporte se está generando y será enviado a los correos
              indicados en cuanto esté listo.
            </Typography>
            <Button
              variant="contained"
              onClick={handleClose}
              style={{
                marginTop: 8,
                backgroundColor: COLOR_PRIMARY,
                color: COLOR_WHITE,
                borderRadius: 8,
                textTransform: "none",
                boxShadow: `0px 4px 12px ${COLOR_PRIMARY_SHADOW}`,
              }}
            >
              Cerrar
            </Button>
          </Box>
        </Fade>
      )}
    </Dialog>
  )
}

export default ExportReportDialog
